import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Loader } from "@/components/kit";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CheckCircle2, MinusCircle, XCircle } from "lucide-react";

const CYCLE = { "": "Available", Available: "Tentative", Tentative: "Blocked", Blocked: "" };
const COLORS = {
  Available: "bg-krida-emerald/20 border-krida-emerald/50 text-krida-emerald",
  Tentative: "bg-krida-amber/20 border-krida-amber/50 text-krida-amber",
  Blocked: "bg-rose-500/20 border-rose-500/50 text-rose-400",
};

export default function Availability() {
  const [cal, setCal] = useState(null);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/me/availability-calendar").then((r) => setCal(r.data.calendar || {})).catch(() => setCal(false)); }, []);
  if (cal === null) return <Loader label="Loading availability" />;
  if (!cal) return <PageHeader title="Availability" subtitle="Available for professional accounts" />;

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const key = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const setDay = async (d) => {
    const k = key(d);
    const next = CYCLE[cal[k] || ""];
    const optimistic = { ...cal };
    if (next) optimistic[k] = next; else delete optimistic[k];
    setCal(optimistic);
    try { await api.put("/me/availability-calendar", { date: k, status: next || null }); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div className="fade-up">
      <PageHeader overline="Availability management" title="My Availability"
        subtitle="Set available, tentative and blocked days. Smart Assign respects your blocked dates." testid="availability-page" />

      <div className="krida-card p-6 max-w-3xl">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="cal-prev" className="p-2 rounded-lg hover:bg-white/5"><ChevronLeft className="w-5 h-5" /></button>
          <div className="font-heading text-xl font-bold">{cursor.toLocaleString("default", { month: "long" })} {y}</div>
          <button onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="cal-next" className="p-2 rounded-lg hover:bg-white/5"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d} className="overline text-slate-500 py-1">{d}</div>)}
          {Array.from({ length: first }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: days }).map((_, i) => {
            const d = i + 1, st = cal[key(d)];
            return (
              <button key={d} onClick={() => setDay(d)} data-testid={`cal-day-${d}`}
                className={`aspect-square rounded-lg border text-sm font-medium transition-colors ${st ? COLORS[st] : "border-white/8 text-slate-400 hover:bg-white/5"}`}>
                {d}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-6 pt-5 border-t border-white/5 text-xs">
          <span className="flex items-center gap-1.5 text-krida-emerald"><CheckCircle2 className="w-4 h-4" /> Available</span>
          <span className="flex items-center gap-1.5 text-krida-amber"><MinusCircle className="w-4 h-4" /> Tentative</span>
          <span className="flex items-center gap-1.5 text-rose-400"><XCircle className="w-4 h-4" /> Blocked</span>
          <span className="text-slate-500 ml-auto">Tap a day to cycle status</span>
        </div>
      </div>
    </div>
  );
}
